import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, AlertTriangle, Activity, Minus, Calendar } from "lucide-react";
import api from "../lib/api";
import { formatRupiah } from "../lib/format";
import { Section, Skeleton, EmptyState, StatCard } from "../components/UI";

const ALERT_STYLE = {
  DANGER:  { cls: "bg-red-50 text-red-700 border-red-200",     icon: AlertTriangle, label: "Inflasi Tinggi" },
  WARNING: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: TrendingUp,    label: "Perlu Dipantau" },
  OK:      { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Activity, label: "Stabil" },
};

export default function ForecastingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(12);
  const [horizon, setHorizon] = useState(30);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forecasting/commodities", {
        params: { weeks, horizon_days: horizon },
      });
      setData(data);
      if (data.commodities.length > 0) setSelected(data.commodities[0].nama);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weeks, horizon]);

  const selectedItem = data?.commodities.find((c) => c.nama === selected);

  // Build chart series: historical + forecast (with gap marker via predicted_only)
  const buildChartData = (item) => {
    if (!item) return [];
    const points = item.series.map((s) => ({
      label: s.week.replace(/^\d{4}-/, "W"),
      actual: s.avg,
    }));
    item.forecast.forEach((f) => {
      points.push({
        label: f.week.replace(/^\d{4}-/, "W"),
        predicted: f.predicted,
      });
    });
    return points;
  };

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Analytics</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Forecasting Harga Komoditas</h1>
          <p className="text-slate-500 text-sm mt-1">
            Prediksi pergerakan harga berdasarkan tren historical PO untuk pengendalian inflasi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">Range</span>
            <select className="text-xs font-medium bg-transparent focus:outline-none" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="filter-weeks">
              <option value={4}>4 minggu</option>
              <option value={8}>8 minggu</option>
              <option value={12}>12 minggu</option>
              <option value={26}>26 minggu</option>
            </select>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md">
            <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">Horizon</span>
            <select className="text-xs font-medium bg-transparent focus:outline-none" value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} data-testid="filter-horizon">
              <option value={7}>7 hari</option>
              <option value={14}>14 hari</option>
              <option value={30}>30 hari</option>
              <option value={60}>60 hari</option>
              <option value={90}>90 hari</option>
            </select>
          </div>
        </div>
      </header>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <>
          {/* Overall stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard icon={Activity} label="Total Komoditas" value={data.overall.total_commodities} sub="Dipantau" accent="blue" testid="stat-total-comm" />
            <StatCard icon={AlertTriangle} label="Alert Inflasi Tinggi" value={data.overall.alerts_danger} sub="Kenaikan ≥ 10%" accent="rose" testid="stat-danger" />
            <StatCard icon={TrendingUp} label="Perlu Dipantau" value={data.overall.alerts_warning} sub="Kenaikan 5-10%" accent="amber" testid="stat-warning" />
            <StatCard
              icon={data.overall.avg_change_pct >= 0 ? TrendingUp : TrendingDown}
              label="Rata-rata Perubahan 4W"
              value={`${data.overall.avg_change_pct >= 0 ? "+" : ""}${data.overall.avg_change_pct}%`}
              sub={data.overall.avg_change_pct >= 5 ? "Indikator inflasi naik" : "Indikator stabil"}
              accent={data.overall.avg_change_pct >= 5 ? "rose" : "emerald"}
              testid="stat-avg-change"
            />
          </div>

          {data.commodities.length === 0 ? (
            <EmptyState title="Belum ada data historical PO" description="Forecasting memerlukan minimal beberapa Purchase Order untuk membentuk tren harga." />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Commodity list */}
              <div className="lg:col-span-1 space-y-2" data-testid="commodity-list">
                <div className="kedan-label text-slate-500 mb-2">Daftar Komoditas</div>
                {data.commodities.map((c) => {
                  const a = ALERT_STYLE[c.alert] || ALERT_STYLE.OK;
                  const Icon = a.icon;
                  return (
                    <button key={c.nama} onClick={() => setSelected(c.nama)}
                      className={`w-full text-left kedan-card p-4 transition-all ${
                        selected === c.nama ? "border-[#045498] ring-2 ring-[#045498]/15 bg-blue-50/30" : "kedan-card-hover"
                      }`}
                      data-testid={`commodity-row-${c.nama.replace(/\s+/g, "-")}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-slate-900 text-sm">{c.nama}</div>
                        <span className={`kedan-badge ${a.cls}`}>
                          <Icon className="w-3 h-3 mr-0.5" />
                          {a.label}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-xs">
                        <div>
                          <div className="text-slate-500">Saat ini</div>
                          <div className="font-mono font-semibold">{formatRupiah(c.latest_price)}/kg</div>
                        </div>
                        <div className="border-l border-slate-200 pl-4">
                          <div className="text-slate-500">Prediksi {horizon}d</div>
                          <div className="font-mono font-semibold">{formatRupiah(c.forecast_price)}</div>
                        </div>
                        <div className="ml-auto">
                          <div className={`text-xs font-semibold ${
                            (c.change_pct_4w || 0) >= 5 ? "text-red-600" :
                            (c.change_pct_4w || 0) > 0 ? "text-amber-600" :
                            (c.change_pct_4w || 0) < 0 ? "text-emerald-600" : "text-slate-500"
                          }`}>
                            {c.change_pct_4w === null ? <Minus className="w-3 h-3 inline" /> :
                              `${c.change_pct_4w >= 0 ? "+" : ""}${c.change_pct_4w}%`}
                          </div>
                          <div className="text-[10px] text-slate-400">vs 4W lalu</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Chart panel */}
              <div className="lg:col-span-2 space-y-4">
                {selectedItem && (
                  <>
                    <div className="kedan-card p-6">
                      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                          <div className="kedan-label text-slate-500">Tren & Prediksi</div>
                          <h3 className="text-2xl font-display font-bold text-slate-900">{selectedItem.nama}</h3>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {selectedItem.data_points} data point · Linear regression
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="kedan-label text-slate-500">Prediksi {horizon} hari</div>
                          <div className="font-display text-2xl font-bold text-[#045498]">
                            {formatRupiah(selectedItem.forecast_price)}
                          </div>
                          <div className={`text-xs font-medium mt-0.5 ${
                            (selectedItem.change_pct_4w || 0) >= 5 ? "text-red-600" :
                            (selectedItem.change_pct_4w || 0) > 0 ? "text-amber-600" : "text-emerald-600"
                          }`}>
                            {selectedItem.change_pct_4w !== null &&
                              `${selectedItem.change_pct_4w >= 0 ? "+" : ""}${selectedItem.change_pct_4w}% (4 minggu)`}
                          </div>
                        </div>
                      </div>
                      <div className="h-72" data-testid="forecast-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={buildChartData(selectedItem)} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip
                              contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}
                              formatter={(v) => v != null ? formatRupiah(v) : "-"}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line
                              type="monotone" dataKey="actual" stroke="#045498" strokeWidth={2}
                              dot={{ r: 3 }} name="Harga Aktual" connectNulls
                            />
                            <Line
                              type="monotone" dataKey="predicted" stroke="#E67E22" strokeWidth={2}
                              strokeDasharray="6 3" dot={{ r: 3 }} name="Prediksi"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="kedan-card p-5">
                      <div className="kedan-label text-slate-500 mb-3">Rekomendasi</div>
                      <div className="space-y-2 text-sm text-slate-700">
                        {selectedItem.alert === "DANGER" && (
                          <div className="bg-red-50 border-l-4 border-red-500 px-3 py-2 rounded">
                            <strong>⚠ Lonjakan harga signifikan.</strong> Pertimbangkan diversifikasi supplier
                            atau intervensi pasar untuk komoditas <strong>{selectedItem.nama}</strong>.
                          </div>
                        )}
                        {selectedItem.alert === "WARNING" && (
                          <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 rounded">
                            <strong>⚠ Tren naik terdeteksi.</strong> Pantau harga {selectedItem.nama} dan
                            siapkan strategi stabilisasi jika tren berlanjut.
                          </div>
                        )}
                        {selectedItem.alert === "OK" && (
                          <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded">
                            <strong>✓ Harga relatif stabil.</strong> Tidak ada tindakan khusus diperlukan
                            untuk komoditas <strong>{selectedItem.nama}</strong>.
                          </div>
                        )}
                        <div className="text-xs text-slate-500 pt-2">
                          Tren slope: <span className="font-mono">{selectedItem.trend_slope >= 0 ? "+" : ""}{selectedItem.trend_slope}</span> Rp/minggu ·
                          Data point: {selectedItem.data_points} ·
                          Metode: Linear Regression (OLS)
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
